import type { SetNonNullable } from "type-fest";
import { notNull } from "@/helpers/utility/Type";

import { useConditionStore } from "../condition/conditions.store";
import { useIncidentStore } from "./incidents.store";
import { useIncidentTriggerStore } from "../incidentTrigger/incidentTriggers.store";
import { useImageStore } from "../image/images.store";
import { useEffectStore } from "../effect/effects.store";

import { getIncidentTriggerCopyData } from "../incidentTrigger/triggerFunctions";
import { getImageCopyData } from "../image/imageFunctions";

import type { IncidentItem } from "./IncidentItem";
import type { ActionItem } from "../action/ActionItem";
import type { DependingIncident } from "./DependingIncident";
import type { ConditionItem } from "../condition/ConditionItem";

export function getDependingIncidents(
  activator: ActionItem | IncidentItem
): DependingIncident[] {
  const conditionStore = useConditionStore();
  const incidentStore = useIncidentStore();
  const incidentTriggerStore = useIncidentTriggerStore();

  const conditions = Object.values(conditionStore.getByActivator(activator));

  const dependingTriggerConditions = conditions
    .filter(
      <T extends ConditionItem>(c: T): c is SetNonNullable<T, "srcId"> => {
        // filter out conditions that are not triggering incidents
        if (c.srcType != "incidentTrigger") return false;
        if (c.srcId == null) return false;

        return true;
      }
    )
    .filter(notNull);

  return dependingTriggerConditions.map((mainCondition) => {
    const trigger = incidentTriggerStore.items[mainCondition.srcId];
    const incident = incidentStore.items[trigger.incidentId];
    const conditions = conditionStore.getBySource(trigger);

    return {
      incident,
      mainCondition,
      conditions,
      otherConditions: conditions.filter((c) => c != mainCondition),
      trigger
    };
  });
}

/**
 * Return all items that are needed to copy an incident.
 */
export function getIncidentCopyData(incident: IncidentItem) {
  const data = [];

  if (incident.imageId != null) {
    const imageStore = useImageStore();
    const image = imageStore.items[incident.imageId];
    data.push(...getImageCopyData(image));
  }

  data.push(incident);

  const incidentTriggerStore = useIncidentTriggerStore();
  const incidentTriggers = incidentTriggerStore.byIncident[incident.id] || [];
  for (const trigger of incidentTriggers) {
    data.push(...getIncidentTriggerCopyData(trigger));
  }

  const effectStore = useEffectStore();
  const effects = effectStore.byIncident[incident.id] || [];
  for (const effect of effects) {
    data.push(effect);
  }

  return data;
}
